import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { Link, NavLink, Route, RouteComponentProps, Switch, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Col,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Nav,
  PopoverBody,
  Row,
  Table,
  UncontrolledPopover,
} from 'reactstrap';
import { ProductModel } from '../../../../shared/orders';
import {
  CreateVoucherModel,
  VoucherListItem,
  VoucherWithDetails,
} from '../../../../shared/orders/voucher';
import {
  ActionButtonModal,
  Breadcrumb,
  ElementSelector,
  Filter,
  FilterTable,
  MaterialIcon,
  PageHeader,
  PermissionBoundary,
  UserStateComponent,
} from '../../../components';
import { Chip } from '../../../components/Chip';
import { Currency } from '../../../components/Currency';
import { DateTime } from '../../../components/Date';
import {
  migrateTimezone,
  NullableTimeZoneInputField,
  TimeZoneInputField,
  trimTime,
} from '../../../components/TimeZoneInputField';
import { UserSelector } from '../../../components/UserSelector';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import {
  displayName,
  Fetcher,
  omit,
  renderName,
  TableKVPair,
  useConfig,
  useConvention,
  useFetcher,
  useForm,
  useInputModel,
  useObject,
} from '../../../utils';
import { charSets, randomString } from '../../../utils/random';
import { AuditObjectPage } from '../audit/log';
import { LocalSearchProvider } from './utils';

export type VoucherProduct = Pick<ProductModel, 'description' | 'displayName' | 'id' | 'name'>;

interface VoucherResponse {
  successCount: number;
}

export const VoucherComponent: FC<RouteComponentProps> = ({ match }) => {
  const { ts } = useTranslation();
  return (
    <UserStateComponent>
      <PageHeader>{ts('common.vouchers')}</PageHeader>
      <Switch>
        <Route component={VoucherComponentTable} exact path={match.path} />
        <Route component={VoucherForm} exact path={`${match.path}/new`} />
        <Route component={VoucherDetails} path={`${match.path}/:id`} />
      </Switch>
    </UserStateComponent>
  );
};

const tableFilters: Filter<VoucherListItem>[] = [
  {
    displayName: 'common.state',
    name: 'common.state',
    options: [
      {
        filter: (voucher) => voucher.maxUses !== -1 && voucher.orderCount >= voucher.maxUses,
        name: 'vouchers.fully_claimed',
        value: 'claimed',
      },
      {
        filter: (voucher) =>
          voucher.deletedAt === null &&
          (voucher.maxUses === -1 || voucher.orderCount < voucher.maxUses),
        name: 'vouchers.available',
        value: 'available',
      },
      {
        filter: (voucher) => !!voucher.deletedAt,
        name: 'common.deleted_archived',
        value: 'deleted',
      },
      {
        filter: (voucher) => voucher.internalOnly,
        name: 'vouchers.internal_vouchers',
        value: 'internal',
      },
    ],
  },
];

const VoucherComponentTable: FC<RouteComponentProps> = ({ match }) => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(async () => {
    return await api.getVouchers();
  });

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      <Link className="button-link" to={`${match.path}/new`}>
        <Button color="primary" id="createVoucher">
          {ts('vouchers.create_new_voucher')}
        </Button>
      </Link>
      <hr />
      <FilterTable<VoucherListItem>
        data={fetcher.data}
        filters={tableFilters}
        infoUrl={match.path}
        renderer={(v) => <VoucherElement voucher={v} />}
      />
    </>
  );
};

const VoucherElement: FC<{ readonly voucher: VoucherListItem }> = ({ voucher }) => {
  return (
    <div className="table-item">
      <MaterialIcon className="icon" large name="loyalty" />
      <p>
        {voucher.displayName ? `${voucher.displayName} (${voucher.code})` : voucher.code} &mdash;{' '}
        <Currency value={voucher.amount} />
      </p>
      <VoucherBadges voucher={voucher} />
    </div>
  );
};

const VoucherBadges: FC<{
  readonly voucher: VoucherListItem;
}> = ({ voucher }) => {
  const { ts } = useTranslation();
  const badges: React.JSX.Element[] = [];
  let voucherAvailable = true;

  if (voucher.deletedAt) {
    return (
      <Badge color="secondary" key="deleted" pill>
        {ts('common.deleted_archived')}
      </Badge>
    );
  }

  if (voucher.availableTo !== null && voucher.availableTo < new Date()) {
    voucherAvailable = false;

    badges.push(
      <Badge color="danger" key="expired" pill>
        {ts('vouchers.expired')} ({voucher.availableTo.toLocaleDateString()})
      </Badge>,
    );
  }

  if (voucher.maxUses !== -1 && voucher.maxUses - voucher.orderCount <= 0) {
    badges.push(
      <Badge color="danger" key="fully_claimed" pill>
        {/* eslint-disable-next-line react/jsx-no-literals */}
        {ts('vouchers.fully_claimed')} (0 / {voucher.maxUses})
      </Badge>,
    );
  } else {
    badges.push(
      <Badge color={voucherAvailable ? 'success' : 'secondary'} key="available" pill>
        {ts('vouchers.available')} ({voucher.orderCount} /{' '}
        {voucher.maxUses === -1 ? 'Unlimited' : voucher.maxUses})
      </Badge>,
    );
  }

  if (voucher.availableTo !== null && voucher.availableTo >= new Date()) {
    badges.push(
      <Badge color="warning" key="expires" pill>
        {ts('vouchers.expires')} ({voucher.availableTo.toLocaleDateString()})
      </Badge>,
    );
  }

  if (voucher.canBeStacked) {
    badges.push(
      <Badge color="info" key="can_be_stacked" pill>
        {ts('vouchers.can_be_stacked')}
      </Badge>,
    );
  }

  if (voucher.internalOnly) {
    badges.push(
      <Badge color="info" key="internal" pill>
        {ts('vouchers.internal')}
      </Badge>,
    );
  }

  return <span className="badge-list compact">{badges}</span>;
};

const VoucherDetails: FC<RouteComponentProps<{ id: string }>> = ({ match }) => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(
    async () => await api.getVoucher(Number.parseInt(match.params.id, 10)),
    [match.params.id],
  );

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  const voucher = fetcher.data;

  return (
    <div>
      <Breadcrumb
        items={[
          { text: ts('common.vouchers'), url: '/housekeeping/vouchers' },
          { text: voucher.code, url: match.path, active: true },
        ]}
      />
      <Nav tabs>
        <NavLink activeClassName="active" className="nav-link" exact to={match.url}>
          {ts('vouchers.summary')}
        </NavLink>
        <NavLink activeClassName="active" className="nav-link" exact to={`${match.url}/orders`}>
          {ts('common.orders')}
        </NavLink>
        <PermissionBoundary inline requiredPermissions="audit:read">
          <NavLink
            activeClassName="active"
            className="nav-link"
            exact
            to={`${match.url}/auditlogs`}
          >
            {ts('vouchers.audit')}
          </NavLink>
        </PermissionBoundary>
        {voucher.deletedAt === null && (
          <NavLink activeClassName="active" className="nav-link" exact to={`${match.url}/issue`}>
            {ts('vouchers.issue')}
          </NavLink>
        )}
        {voucher.deletedAt === null && (
          <NavLink activeClassName="active" className="nav-link" exact to={`${match.url}/manage`}>
            {ts('common.edit')}
          </NavLink>
        )}
      </Nav>
      <Switch>
        <Route
          exact
          path={`${match.path}/orders`}
          render={() => <VoucherOrders voucher={voucher} />}
        />
        <Route
          exact
          path={`${match.path}/issue`}
          render={() => <VoucherIssue refresh={fetcher.refresh} voucher={voucher} />}
        />
        <Route
          exact
          path={`${match.path}/auditlogs`}
          render={() => <AuditObjectPage entityId="Voucher" objectId={voucher.id.toString()} />}
        />
        {voucher.deletedAt === null && (
          <Route
            exact
            path={`${match.path}/manage`}
            render={() => <VoucherManage refresh={fetcher.refresh} voucher={voucher} />}
          />
        )}
        <Route render={() => <VoucherDetailsSummary voucher={voucher} />} />
      </Switch>
    </div>
  );
};

const VoucherDetailsSummary: FC<{ readonly voucher: VoucherWithDetails }> = ({ voucher }) => {
  const { ts } = useTranslation();
  return (
    <Table id="voucherSummary">
      <tbody>
        <TableKVPair
          displayName="common.value"
          name="amount"
          value={<Currency value={voucher.amount} />}
        />
        <TableKVPair
          displayName="common.available_from"
          name="availableFrom"
          value={<DateTime value={voucher.availableFrom} />}
        />
        <TableKVPair
          displayName="vouchers.available_to"
          name="availableTo"
          value={voucher.availableTo ? <DateTime value={voucher.availableTo} /> : <i>{ts('na')}</i>}
        />
        <TableKVPair displayName="common.code" name="code" value={voucher.code} />
        <TableKVPair displayName="issuer_id" name="issuer" value={voucher.issuerId} />
        <TableKVPair
          displayName="vouchers.max_number_of_uses"
          name="maxUses"
          value={voucher.maxUses}
        />
        <TableKVPair
          displayName="vouchers.can_voucher_be_stacked"
          name="canBeStacked"
          value={ts(voucher.canBeStacked ? 'common.yes' : 'common.no')}
        />
        <TableKVPair
          displayName="vouchers.total_uses"
          name="totalUses"
          value={voucher.orders.length}
        />
        <TableKVPair
          displayName="common.created_at"
          name="createdAt"
          value={<DateTime value={voucher.createdAt} />}
        />
        {voucher.productsRestriction.length > 0 && (
          <TableKVPair
            displayName="vouchers.product_restrictions"
            name="productsRestriction"
            value={voucher.productsRestriction.map((p) => (
              <Link key={p.id} to={`/housekeeping/products/${p.id}`}>
                <Chip<number> id={`productChip${p.id}`} value={p.id}>
                  {`${displayName(p)} (ID: ${p.id})`}
                </Chip>{' '}
              </Link>
            ))}
          />
        )}
      </tbody>
    </Table>
  );
};

const VoucherOrders: FC<{ readonly voucher: VoucherWithDetails }> = ({ voucher }) => {
  const { ts } = useTranslation();

  return (
    <Table>
      <thead>
        <tr>
          <th>{ts('common.user')}</th>
          <th>{ts('common.order_id')}</th>
          <th>{ts('common.order_status')}</th>
          <th>{ts('vouchers.paid_at')}</th>
        </tr>
      </thead>
      <tbody>
        {voucher.orders.map((order) => (
          <tr key={order.id}>
            <td>
              <Link to={`/housekeeping/attendees/user/${order.user.id}`}>
                {renderName(order.user)}
              </Link>
            </td>
            <td>
              <Link to={`/housekeeping/orders/${order.id}`}>{order.id}</Link>
            </td>
            <td>{order.status}</td>
            <td>{order.paidAt && <DateTime value={order.paidAt} />}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const VoucherIssue: FC<{ readonly voucher: VoucherWithDetails; readonly refresh: () => void }> = ({
  voucher,
  refresh,
}) => {
  const { ts } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const onVoucherIssue = useCallback(
    ({ successCount }: VoucherResponse) => {
      if (successCount > 0) {
        toast.success(ts('vouchers.voucher_has_been_issued', voucher.id, successCount));
      }

      refresh();
    },
    [refresh, ts, voucher.id],
  );

  return (
    <JSONForm<VoucherResponse, { userIds: number[] }>
      method="post"
      onSuccess={onVoucherIssue}
      path={`/api/vouchers/${voucher.id}/issue`}
      preSubmit={(r) => (r.inputs.userIds = selectedUsers)}
    >
      <p>{ts('vouchers.issue_voucher_all_users', voucher.id)}</p>
      <FormGroup>
        <Label for="otherRecipients">{ts('common.user_sing_plural')}</Label>
        <UserSelector id="customRecipients" selectionIdsChanged={setSelectedUsers} />
      </FormGroup>
      <FormGroup check className="mt-10 start-align" id="fulfillOrdersGroup">
        <Input id="fulfillOrders" name="fulfillOrders" type="checkbox" />
        <Label check for="fulfillOrders">
          {ts('vouchers.attempt_fulfill_associated_orders')}
        </Label>
        <UncontrolledPopover placement="bottom" target="fulfillOrdersGroup" trigger="hover">
          <PopoverBody>
            <p>{ts('vouchers.this_option_will_attempt_to_fulfill_the_latest_order_of')}</p>
            <p>{ts('vouchers.remaining_balance_greater_zero_no_items')}</p>
          </PopoverBody>
        </UncontrolledPopover>
      </FormGroup>

      <Button color="primary" id="issueVoucherBtn" type="submit">
        {ts('vouchers.issue_voucher')}
      </Button>
    </JSONForm>
  );
};

const VoucherManage: FC<{ readonly voucher: VoucherWithDetails; readonly refresh: () => void }> = ({
  voucher,
  refresh,
}) => {
  const { ts } = useTranslation();
  return (
    <>
      <div>
        <h2>{ts('voucher.edit')}</h2>
        <p>{ts('voucher.you_can_edit')}</p>
        <VoucherForm refresh={refresh} voucher={voucher} />
      </div>
      <div>
        <h2>{ts('voucher.delete')}</h2>
        <p>
          {ts('voucher.if_you_would_like_to_delete')} {voucher.displayName ?? voucher.code},
          {ts('voucher.please_click_button')}
        </p>
        <p>
          {ts('voucher.deleting_a_voucher')}
          <strong>{ts('permanent')}</strong> {ts('voucher.action_cannot_be_undone')}
        </p>
        <ActionButtonModal
          actionContent="common.delete"
          buttonContent="common.delete"
          color="danger"
          id="completeDelete"
          onComplete={async () => {
            await api.deleteVoucher(voucher.id);
            toast.success(ts('vouchers.voucher_has_been_deleted_successfully'));
            refresh();
          }}
          title="voucher.delete"
        >
          {ts('are_you_sure_you_want_to_delete_the_voucher')}
        </ActionButtonModal>
      </div>
    </>
  );
};

function randomVoucher(): string {
  return `${randomString(5, charSets.code)}-${randomString(5, charSets.code)}-${randomString(
    5,
    charSets.code,
  )}`;
}

const VoucherForm: FC<{
  readonly voucher?: VoucherWithDetails;
  readonly refresh?: () => void;
}> = ({ voucher: inputVoucher, refresh }) => {
  const { ts } = useTranslation();
  const [voucher, setVoucher] = useState<CreateVoucherModel>(() => ({
    canBeStacked: inputVoucher?.canBeStacked ?? false,
    code: inputVoucher?.code ?? randomVoucher(),
    displayName: inputVoucher?.displayName ?? '',
    amount: (inputVoucher?.amount ?? 0) / 100,
    maxUses: inputVoucher?.maxUses ?? -1,
    availableFrom: inputVoucher?.availableFrom ?? new Date(),
    availableTo: inputVoucher?.availableTo ?? null,
    productsRestriction: inputVoucher?.productsRestriction.map((t) => t.id) ?? [],
  }));

  const { timeZone } = useConvention();
  const history = useHistory();

  const { currencySymbol } = useConfig();
  const defaultDate = migrateTimezone(trimTime(new Date()), timeZone);
  const setVoucherCodeEV = useInputModel(setVoucher, 'code');
  const setDisplayName = useInputModel(setVoucher, 'displayName');
  const setAmount = useInputModel(setVoucher, 'amount');
  const setMaxUses = useInputModel(setVoucher, 'maxUses');
  const [, setAvailableFrom] = useObject(voucher, setVoucher, 'availableFrom');
  const [, setAvailableTo] = useObject(voucher, setVoucher, 'availableTo');
  const setCanBeStacked = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setVoucher((old) => ({ ...old, canBeStacked: e.target.checked }));
    },
    [setVoucher],
  );

  const setRandomVoucherCode = useCallback(() => {
    setVoucher((old) => ({ ...old, code: randomVoucher() }));
  }, [setVoucher]);

  const productList = useFetcher(async () => {
    return await api.getProducts();
  }, []);

  const memoizedSearchProvider = useMemo(
    () => new LocalSearchProvider(() => productList.data ?? []),
    [productList.data],
  );

  const form = useForm(async () => {
    if (inputVoucher) {
      await api.updateVoucher(inputVoucher.id, omit(voucher, 'code', 'amount'));
      toast.success(ts('voucher_has_been_updated'));
      if (refresh) {
        refresh();
      }
    } else {
      const { id } = await api.createVoucher({
        ...voucher,
        displayName: voucher.displayName?.length === 0 ? null : voucher.displayName,
      });

      toast.success(ts('vouchers.new_voucher_has_been_created'));
      history.push(`/housekeeping/vouchers/${id}`);
    }
  }, [inputVoucher, voucher, ts, refresh, history]);

  if (!productList.success) {
    return <Fetcher result={productList} />;
  }

  return (
    <>
      {inputVoucher && (
        <Breadcrumb
          items={[
            { text: ts('common.vouchers'), url: '/housekeeping/vouchers' },
            { text: ts('common.create'), url: '/housekeeping/vouchers/new', active: true },
          ]}
        />
      )}
      <form onSubmit={form.onSubmit}>
        <Row id="createVoucherForm">
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="name">{ts('vouchers.voucher_code')}</Label>
              <InputGroup>
                <Button color="primary" onClick={setRandomVoucherCode}>
                  <MaterialIcon name="casino" small />
                </Button>
                <Input
                  id="code"
                  onChange={setVoucherCodeEV}
                  placeholder={ts('vouchers.voucher_code')}
                  readOnly={!!inputVoucher}
                  required
                  value={voucher.code}
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="displayName">
                {ts('common.display_name')}
                <small> {ts('common.optional')}</small>
              </Label>
              <Input id="displayName" onChange={setDisplayName} value={voucher.displayName ?? ''} />
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="name">{ts('common.amount')}</Label>
              <InputGroup>
                <InputGroupText>{currencySymbol}</InputGroupText>
                <Input
                  id="amount"
                  min={0.01}
                  onChange={setAmount}
                  readOnly={!!inputVoucher}
                  required
                  step={0.01}
                  type="number"
                  value={voucher.amount}
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="name">{ts('vouchers.max_uses')}</Label>
              <Input
                id="maxUses"
                min={-1}
                onChange={setMaxUses}
                required
                type="number"
                value={voucher.maxUses}
              />
              <FormText color="muted">{ts('vouchers.unlimited_uses')}</FormText>
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="availableFrom">{ts('common.available_from')}</Label>
              <TimeZoneInputField
                defaultValue={voucher.availableFrom}
                id="availableFrom"
                onChange={setAvailableFrom}
                required
                timeZone={timeZone}
              />
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="availableTo">{ts('common.available_until')}</Label>
              <NullableTimeZoneInputField
                defaultValue={voucher.availableTo}
                id="availableTo"
                min={defaultDate}
                onChange={setAvailableTo}
                timeZone={timeZone}
              />
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="availableTo">{ts('vouchers.restrict_voucher_products')}</Label>
              <ElementSelector<VoucherProduct>
                defaultSelected={[]}
                id="productSelector"
                searchPlaceholder={ts('common.searchPlaceholder')}
                searchProvider={memoizedSearchProvider}
                selectionChanged={(t) => {
                  setVoucher({ ...voucher, productsRestriction: t.map((p) => p.id) });
                }}
              />
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup check className="mt-5">
              <Input
                defaultChecked={voucher.canBeStacked}
                id="canBeStacked"
                onChange={setCanBeStacked}
                type="checkbox"
              />
              <Label check for="canBeStacked">
                {ts('vouchers.allow_voucher_stacking')}
              </Label>
            </FormGroup>
            <FormText className="mt-10" color="muted">
              {ts('voucher_stacking_allows_users')}
            </FormText>
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col lg={8} xs={12}>
            <Button
              block
              color="primary"
              disabled={form.saving}
              id="submitNewVoucher"
              type="submit"
            >
              {ts(inputVoucher ? 'voucher.update' : 'vouchers.create_voucher')}
            </Button>
          </Col>
        </Row>
      </form>
    </>
  );
};
